import { Button, Form } from 'antd';
import { FC, memo, useCallback, useState } from 'react';
import { Plus } from 'react-bootstrap-icons';
import { useIsFetching, useQueryClient } from 'react-query';

import { AppMessage } from '@/common/components';
import { FormFieldsErrors } from '@/common/interfaces';
import { extractFormErrors, messageOnError } from '@/common/utils';

import { HostingAccountFormFields, HostingAccountFormInputs, HostingAccountsQueryKeys, useCreateHostingAccountsMutation } from '@/modules/Hostings';

import { HostingAccountsFormView } from './HostingAccountsFormView';
import { hostingAccountFormValidationSchema } from './validation';

interface Props {
  hostingId: string;
}

export const HostingAccountsCreateFormContainer: FC<Props> = memo(({ hostingId }) => {
  const queryClient = useQueryClient();
  const appMessageApi = AppMessage.useControls();
  const [form] = Form.useForm<HostingAccountFormFields>();
  const [formErrors, setFormErrors] = useState<FormFieldsErrors<typeof HostingAccountFormInputs>>(null);
  const [isCreateAccountFormVisible, setIsCreateAccountFormVisible] = useState(false);

  const { mutate: createHostingAccountMutation, isLoading: createHostingAccountIsLoading } =
    useCreateHostingAccountsMutation(hostingId)({
      onSuccess: async () => {
        appMessageApi.success('Акаунт успішно створено!');
        await queryClient.refetchQueries([HostingAccountsQueryKeys.GetAll, hostingId], {
          exact: true
        });
        handleBack();
      },
      onError: (err) => {
        setFormErrors(extractFormErrors<typeof HostingAccountFormInputs>(err));
        messageOnError(err, appMessageApi);
      }
    });

  const initialValues: HostingAccountFormFields = { login: undefined, password: undefined };

  const isHostingAccountsListError = !!queryClient.getQueryState([HostingAccountsQueryKeys.GetAll], { exact: true })
    ?.fetchFailureCount;

  const isHostingAccountsListFetching = !!useIsFetching({
    queryKey: [HostingAccountsQueryKeys.GetAll, hostingId],
    exact: true
  });

  const handleFinish = useCallback((values: HostingAccountFormFields): void => {
    setFormErrors(null);
    createHostingAccountMutation(values);
  }, []);

  const handleFinishFailed = useCallback((): void => {
    setFormErrors(null);
  }, []);

  const handleShowAccountForm = (): void => {
    setIsCreateAccountFormVisible(true);
  };

  const handleBack = useCallback((): void => {
    form.resetFields();
    setIsCreateAccountFormVisible(false);
  }, []);

  return (
    <>
      { isCreateAccountFormVisible ? (
        <HostingAccountsFormView
          form={ form }
          formName={ 'hosting-account-create' }
          formType={ 'create' }
          initialValues={ initialValues }
          isFormLoading={ createHostingAccountIsLoading }
          validationSchema={ hostingAccountFormValidationSchema }
          formErrors={ formErrors }
          handleBack={ handleBack }
          handleFinish={ handleFinish }
          handleFinishFailed={ handleFinishFailed }
        />
      ) : (
        <Button
          block
          type="dashed"
          disabled={ isHostingAccountsListFetching || isHostingAccountsListError }
          icon={ <Plus/> }
          onClick={ handleShowAccountForm }
        >
          Додати акаунт
        </Button>
      ) }
    </>
  );
});
